import { graphql } from 'gql.tada';

export const GET_PROGRAMS = graphql(`
    query searchPrograms(
        $audioLanguages: [LanguageISOCode!]
        $awards: String
        $categoryIds: [UUID!]
        $directors: String
        $genres: [UUID!]
        $keywords: String
        $language: LanguageISOCode
        $limit: Int
        $maximumDuration: Int
        $maximumEpisodesQuantity: Int
        $maximumFirstBroadcastDate: DateTime
        $maximumLicensesQuantity: Int
        $maximumPrice: Float
        $minimumDuration: Int
        $minimumEpisodesQuantity: Int
        $minimumFirstBroadcastDate: DateTime
        $minimumLicensesQuantity: Int
        $minimumPrice: Float
        $offset: Int
        $orderBy: ProgramOrderBy
        $parentId: UUID = null
        $productionCountries: [CountryISOCode!]
        $quality: Quality
        $published: Boolean
        $sellerId: UUID
        $sellerLegalName: String
        $excludedGenres: [UUID!]
        $sku: Int
        $subtitleLanguage: [LanguageISOCode!]
        $synopsis: String
        $title: String
        $validated: Boolean
        $includeSensitiveData: Boolean! = false
        $includeMasterFiles: Boolean! = false
        $includeSellerData: Boolean! = false
    ) {
        searchPrograms(
            audioLanguages: $audioLanguages
            awards: $awards
            categoryIds: $categoryIds
            genres: $genres
            keywords: $keywords
            excludedGenres: $excludedGenres
            language: $language
            limit: $limit
            maximumDuration: $maximumDuration
            maximumEpisodesQuantity: $maximumEpisodesQuantity
            maximumFirstBroadcastDate: $maximumFirstBroadcastDate
            maximumLicensesQuantity: $maximumLicensesQuantity
            maximumPrice: $maximumPrice
            minimumDuration: $minimumDuration
            minimumEpisodesQuantity: $minimumEpisodesQuantity
            minimumFirstBroadcastDate: $minimumFirstBroadcastDate
            minimumLicensesQuantity: $minimumLicensesQuantity
            minimumPrice: $minimumPrice
            productionCountries: $productionCountries
            quality: $quality
            sellerId: $sellerId
            sellerLegalName: $sellerLegalName
            sku: $sku
            subtitleLanguage: $subtitleLanguage
            synopsis: $synopsis
            title: $title
            validated: $validated
            offset: $offset
            parentId: $parentId
            directors: $directors
            published: $published
            orderBy: $orderBy
        ) {
            count
            Programs {
                audioLanguages
                awards
                casting
                category {
                    catchPhrase
                    id
                    image
                    title
                }
                countriesRight
                directors
                coverUrl
                creditRollUrl
                duration
                episodeNumber
                episodesQuantity
                firstBroadcastDate
                genres {
                    title
                    id
                }
                identifier {
                    id
                    sku
                }
                seller @include(if: $includeSellerData) {
                    legalName
                    identifier {
                        id
                        sku
                    }
                }
                imdb
                keywords
                licensesQuantity
                parent {
                    identifier {
                        id
                        sku
                    }
                    coverUrl
                }
                price
                productionCountryIsoCode
                quality
                synopsis
                titles
                trailerFile {
                    id
                    tenant {
                        cloudFrontUrl
                    }
                }
                trailerSubtitles {
                    language
                    sourceFile {
                        id
                        tenant {
                            cloudFrontUrl
                        }
                    }
                }
                masterFile @include(if: $includeMasterFiles) {
                    id
                    details
                    tenant {
                        cloudFrontUrl
                    }
                }
                masterSubtitles @include(if: $includeMasterFiles) {
                    language
                    sourceFile {
                        id
                        tenant {
                            cloudFrontUrl
                        }
                    }
                }
                validated @include(if: $includeSensitiveData)
                published @include(if: $includeSensitiveData)
                coverImageFile {
                    tenant {
                        cloudFrontUrl
                    }
                    id
                }
            }
        }
    }
`);

export const GET_PROGRAM = graphql(`
    query program($id: UUID!, $includeSensitiveData: Boolean! = false, $includeMasterFiles: Boolean! = false) {
        program(id: $id) {
            audioLanguages
            awards
            casting
            isExclusive
            isSegmentable
            category {
                catchPhrase
                id
                image
                title
            }
            countriesRight
            creditRollUrl
            coverUrl
            directors
            duration
            publicSeller {
                legalName
            }
            episodeNumber
            episodesQuantity
            excludedCountries @include(if: $includeSensitiveData) {
                countryISOCode
                exclusionType
            }
            published @include(if: $includeSensitiveData)
            validated @include(if: $includeSensitiveData)
            firstBroadcastDate
            genres {
                title
                id
            }
            parent {
                identifier {
                    id
                    sku
                }
                creditRollUrl
                coverUrl
            }
            identifier {
                id
                sku
            }
            imdb
            keywords
            licensesQuantity
            price
            productionCountryIsoCode
            quality
            synopsis
            titles
            coverImageFile {
                tenant {
                    cloudFrontUrl
                }
                id
            }
            trailerFile {
                id
                tenant {
                    cloudFrontUrl
                }
            }
            trailerSubtitles {
                language
                sourceFile {
                    id
                    tenant {
                        cloudFrontUrl
                    }
                }
            }
            masterFile @include(if: $includeMasterFiles) {
                id
                details
                tenant {
                    cloudFrontUrl
                }
            }
            masterSubtitles @include(if: $includeMasterFiles) {
                language
                sourceFile {
                    id
                    tenant {
                        cloudFrontUrl
                    }
                }
            }
            creditRollFile @include(if: $includeMasterFiles) {
                tenant {
                    cloudFrontUrl
                }
                id
                fileExtension
            }
        }
    }
`);

export const UPDATE_PROGRAM = graphql(`
    mutation updateProgram(
        $id: UUID!,
        $awards: String,
        $casting: String,
        $categoryId: UUID,
        $directors: String,
        $episodeNumber: Int,
        $excludedCountries: [ExcludedCountryInput!],
        $firstBroadcastDate: DateTime,
        $genreIds: [UUID!],
        $isSegmentable: Boolean,
        $isExclusive: Boolean,
        $imdb: String,
        $keywords: Keywords,
        $productionCountry: CountryISOCode,
        $published: Boolean,
        $synopsis: Synopsis,
        $titles: Titles,
        $validated: Boolean,
    ) {
        updateProgram(
            id: $id,
            awards: $awards,
            casting: $casting,
            categoryId: $categoryId,
            directors: $directors,
            episodeNumber: $episodeNumber,
            excludedCountries: $excludedCountries,
            firstBroadcastDate: $firstBroadcastDate,
            genreIds: $genreIds,
            imdb: $imdb,
            keywords: $keywords,
            productionCountry: $productionCountry,
            published: $published,
            synopsis: $synopsis,
            titles: $titles,
            validated: $validated,
            isSegmentable: $isSegmentable,
            isExclusive: $isExclusive,
        ) {
            identifier {
                id
                sku
            }

        }
    }
`);

export const DELETE_PROGRAM = graphql(`
    mutation deleteProgram($id: String!) {
        deleteProgram(id: $id) {
            identifier {
                id
            }
        }
    }
`);
